import { type CSSProperties, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import { format } from "date-fns";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { buildXContextHeader } from "@utils/api/headers";
import { getOrderDetails } from "@screen/Home/pages/OrderList/API/handleOrderAPI";
import { v2 } from "./skin";
import ReceiptPaper from "@components/layout/ReceiptPaper";

// require 在部分打包設定下回傳的是 { default: url } 而非字串，取不到就會變成 [object Object]
const mascotModule = require("@styles/images/gas-mascot.png");
const MascotImg = (mascotModule?.default ?? mascotModule) as string;

/** 慶祝火花的四個方向（往斜上與兩側彈開；不往正下方——那邊接著收據，火花會撞上去）。 */
const SPARKS = [
  { "--sx": "-52px", "--sy": "-40px", "--sr": "-28deg" },
  { "--sx": "52px", "--sy": "-40px", "--sr": "28deg" },
  { "--sx": "-66px", "--sy": "2px", "--sr": "-72deg" },
  { "--sx": "66px", "--sy": "2px", "--sr": "72deg" },
] as unknown as CSSProperties[];

interface Receipt {
  displayId: string | null;
  total: number | null;
  createdAt: string | null;
  paymentLabel: string | null;
}

/**
 * 下單成功頁（/gasShop/done）。
 *
 * 為什麼要有：送單成功原本直接跳訂單詳情，客戶少了「成功了」這個時刻——畫面一換就是一張
 * 訂單，分不清是送出了還是只是看到舊資料。
 *
 * 版面沿用「彩色帶 + 壓在色帶下緣的收據卡」這個成交畫面的通用結構：
 * 上半是慶祝（吉祥物 + 標題），下半是可核對的事實（金額、單號、時間、付款方式）。
 *
 * orderId 走 navigate state 而不是網址參數：這頁是流程的終點、不需要被分享或重整，
 * 用 state 可以讓路徑保持固定字串，PageWrapper 那幾份以路徑精確比對的設定（標題、不顯示
 * 返回鍵）才對得上。重整或直接開網址時沒有 state，直接退回訂購頁。
 *
 * 不自動跳轉：由客戶自己按「查看訂單」或「回首頁」離開。
 * 導頁一律 replace：成功頁是一次性的，留在 history 裡的話客戶從訂單詳情按上一頁會回到這裡。
 *
 * 動畫定義在 all.scss 的 .gas-done-*：全程 transform / opacity，走合成層不觸發重排，
 * 並在 prefers-reduced-motion 時整組關閉。
 */
const GasShopOrderDone = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const { hideAmountOnPending } = useOrder();
  const orderId = (location.state as { orderId?: string } | null)?.orderId;

  const [receipt, setReceipt] = useState<Receipt | null>(null);

  // 明細：向訂單詳情 API 取，不從結帳頁一路傳參數過來——金額要以後端算出的
  // total_price_cached 為準（含樓層費、優惠券等），前端自行加總容易與訂單頁對不上。
  useEffect(() => {
    if (!orderId || !cusJwtToken) return;
    let alive = true;
    getOrderDetails({ orderId, jwtToken: cusJwtToken, xContext: buildXContextHeader(chooseIdentity) ?? undefined })
      .then((res) => {
        if (!alive) return;
        if (res.state !== "success") {
          setReceipt({ displayId: null, total: null, createdAt: null, paymentLabel: null });
          return;
        }
        const data = res.data?.data;
        const cached = data?.supplier_order_snapshot_json?.order_list?.total_price_cached;
        setReceipt({
          displayId: data?.supplier_order_snapshot_json?.order_list?.order_id ?? null,
          total: cached != null ? Number(cached) : null,
          createdAt: data?.create_time_stamp ?? null,
          paymentLabel: data?.payer_snapshot_json?.label ?? null,
        });
      })
      .catch(() => {
        if (alive) setReceipt({ displayId: null, total: null, createdAt: null, paymentLabel: null });
      });
    return () => {
      alive = false;
    };
  }, [orderId, cusJwtToken, chooseIdentity]);

  // 沒有 orderId（重整／直接開網址）→ 這頁沒有意義，退回訂購頁
  useEffect(() => {
    if (!orderId) navigate("/gasShop/products", { replace: true });
  }, [orderId, navigate]);

  if (!orderId) return null;

  // 參考稿的欄位是「標籤：值」一行到底，標籤自帶全形冒號、值緊接其後——
  // 不是左右分推、也不是固定寬的兩欄表格，收據的讀法就是一行純文字。
  const rows: { k: string; v: string }[] = [
    { k: "訂單編號", v: receipt?.displayId || "—" },
    { k: "下單時間", v: receipt?.createdAt ? format(new Date(receipt.createdAt), "yyyy/MM/dd HH:mm:ss") : "—" },
    { k: "支付方式", v: receipt?.paymentLabel || "—" },
  ];

  return (
    <YStack flex={1}>
      {/* 上半：慶祝。吉祥物與標題橫排，坐在頁面頂端的淡霧裡——那塊本來是空的。 */}
      {/* 不再做視覺補償：原本往左推 12px，是因為白字標題在實藍上偏輕、重心落在吉祥物那側。
          換成淡霧底之後標題變成深藍粗體、比吉祥物還重，那 12px 就從補償變成把整組推歪。
          現在幾何置中即視覺置中。 */}
      <XStack ai="center" jc="center" gap={2} className="gas-done-pop">
        <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div
            className="gas-done-halo"
            style={{
              position: "absolute",
              width: 130,
              height: 130,
              borderRadius: "50%",
              // 淡底上的白色光暈等於看不見，改成淡藍讓吉祥物後面仍有一圈托底
              background: "radial-gradient(circle, rgba(150,197,242,0.45) 0%, rgba(150,197,242,0) 70%)",
              pointerEvents: "none",
            }}
          />
          {/* 鵝黃火花：四道短棒從吉祥物身後往外彈開。
              用這個顏色不是憑空引進新色相——角色圖裡頭上那兩道驚嘆號本來就是黃的，
              這裡只是把它延伸到畫面上。鵝黃是裝飾色，狀態的琥珀（colors.amber）明度深一大階。
              位移與角度用 CSS 變數帶進 keyframes（見 all.scss 的 gas-done-spark），
              四道各自的方向才不必寫四組動畫。 */}
          {SPARKS.map((sp, i) => (
            <span
              key={i}
              aria-hidden
              className="gas-done-spark"
              style={{ ...sp, animationDelay: `${0.12 + i * 0.04}s` } as CSSProperties}
            />
          ))}
          <img
            src={MascotImg}
            alt=""
            style={{ width: 118, height: 118, objectFit: "contain", display: "block", position: "relative" }}
          />
        </div>
        <YStack>
          <Text fontSize={24} fontWeight="800" color={v2.hazeInk}>
            訂單已送出
          </Text>
          <Text fontSize={13} color={v2.hazeSub} mt={2}>
            瓦斯行確認後會通知你
          </Text>
        </YStack>
      </XStack>

      {/* 下半：可核對的事實，做成一張印出來的收據（版面與付費明細共用，見 ReceiptPaper） */}
      <ReceiptPaper
        loading={!receipt}
        // 隱藏金額的瓦斯行不顯示金額（待確認前不揭露），改放單號當主秀
        amount={!hideAmountOnPending && receipt?.total != null ? receipt.total : null}
        fallbackTitle={receipt?.displayId || "訂單已建立"}
        rows={rows}
      />

      {/* 主要動作：置中的單顆膠囊；「回首頁」降成底下的文字連結，成交畫面只該有一個主要動作。 */}
      <YStack ai="center" mt={38} className="gas-done-rise-4">
        <YStack
          backgroundColor={colors.primary}
          borderRadius={999}
          height={50}
          px={62}
          ai="center"
          jc="center"
          cursor="pointer"
          pressStyle={{ opacity: 0.85 }}
          onPress={() => navigate(`/orders/${orderId}`, { replace: true })}>
          <Text fontSize={16} fontWeight="800" color="#fff">
            查看訂單
          </Text>
        </YStack>

        <Text fontSize={12} color={v2.sub} mt={16}>
          {receipt ? "瓦斯行接單後會用 LINE 通知你" : "整理訂單明細…"}
        </Text>

        <Text
          fontSize={13}
          color={v2.sub}
          mt={20}
          cursor="pointer"
          pressStyle={{ opacity: 0.6 }}
          onPress={() => navigate("/home", { replace: true })}>
          回首頁
        </Text>
      </YStack>
    </YStack>
  );
};

export default GasShopOrderDone;
